import {
  replyToEstimate,
  updateEstimateReply,
  updateEstimatePreferredProductPrices,
} from "@/api/providerDashboard";
import {
  formatEstimateReplyPriceInput,
  formatPriceWithCommas,
  isValidEstimateReplyPrice,
  normalizeEstimateReplyPrice,
} from "@/utils/priceFormat";
import {
  getReplyProductsMinimumPrice,
  normalizeReplyProductMaterialPrices,
  normalizeReplyProductPrice,
} from "@shared/replyProductPrice.cjs";

export default {
  initializeReplyForm() {
    const estimate = this.estimate || {};
    const reply = estimate.reply || {};
    const templates = this.messageTemplates || [];
    const description =
      reply.description !== undefined
        ? reply.description
        : estimate.comment || (templates[0] && templates[0].message) || "";
    this.replyForm = {
      price: reply.price || estimate.price || "",
      period: reply.period || estimate.period || "",
      description,
      images: Array.isArray(reply.images) ? reply.images.slice() : [],
      preferredProductPrices: Array.isArray(reply.preferredProductPrices)
        ? reply.preferredProductPrices.map((item) => ({ ...item }))
        : [],
      products: [],
    };
    const selected = templates.find((item) => item.message === description);
    this.selectedTemplateId = selected ? selected.id : "";
    this.initializeReplyProducts();
  },
  startEditReply() {
    if (
      this.submitting ||
      !this.estimate ||
      this.estimate.status !== "response"
    )
      return;
    if (this.isComparisonEstimate && this.estimate.receivingEnabled === false) {
      return this.$alert("고객이 견적 받기를 중지했습니다.");
    }
    this.editingReply = true;
    this.initializeReplyForm();
    if (this.isComparisonEstimate) return;
    const referenceImages = Array.isArray(this.estimate.images)
      ? this.estimate.images
      : [];
    referenceImages.forEach((image, index) => {
      if (typeof image !== "string" || !image.trim()) return;
      const imageUrl = image.trim();
      const id = `request-photo-${index + 1}`;
      if (
        this.replyForm.products.some(
          (product) =>
            product.id === id ||
            product.imageUrl === imageUrl ||
            product.imageUrls.includes(imageUrl)
        )
      )
        return;
      this.replyForm.products.push({
        id,
        productId: "",
        name: `고객 첨부 사진 ${index + 1}`,
        description: "",
        imageUrl,
        imageUrls: [imageUrl],
        price: "",
        material: { base: "14K", additionalPrice: "" },
        color: "white",
        diamonds: [],
        promotion: { enabled: false, price: "" },
      });
    });
    this.setReplyProducts(this.replyForm.products);
  },
  cancelEditReply() {
    if (this.submitting || this.uploadingProductImage) return;
    this.editingReply = false;
    this.initializeReplyForm();
  },
  initializeReplyProducts() {
    const reply = (this.estimate && this.estimate.reply) || {};
    if (Array.isArray(reply.products) && reply.products.length) {
      this.replyForm.products = reply.products.map((item) => ({
        ...item,
        material: { ...(item.material || {}) },
        promotion: { ...(item.promotion || { enabled: false, price: "" }) },
        imageUrls: Array.isArray(item.imageUrls) ? item.imageUrls.slice() : [],
        diamonds: Array.isArray(item.diamonds)
          ? item.diamonds.map((diamond) => ({ ...diamond }))
          : [],
      }));
      this.setReplyProducts(this.replyForm.products, true);
      return;
    }
    const storedPrices = Array.isArray(reply.preferredProductPrices)
      ? reply.preferredProductPrices
      : [];
    const referenceImages = Array.isArray(this.estimate && this.estimate.images)
      ? this.estimate.images.filter(
          (image) => typeof image === "string" && image.trim()
        )
      : [];
    const sourceProducts = this.isComparisonEstimate
      ? []
      : (this.preferredProducts || []).length
      ? this.preferredProducts.map((product) => ({
          ...product,
          productId: "",
        }))
      : referenceImages.length && !this.isProductEstimate
      ? []
      : [
          {
            id:
              (this.estimate.product &&
                (this.estimate.product.id || this.estimate.product._id)) ||
              "request-product",
            productId:
              (this.estimate.product &&
                (this.estimate.product.id || this.estimate.product._id)) ||
              "",
            name:
              this.estimate.productName ||
              (this.estimate.product && this.estimate.product.name) ||
              "",
            imageUrl:
              this.estimate.productImage ||
              (this.estimate.product &&
                Array.isArray(this.estimate.product.imagesUrls) &&
                this.estimate.product.imagesUrls[0]) ||
              "",
          },
        ];
    const seenImages = new Set(
      sourceProducts.map((product) => product.imageUrl).filter(Boolean)
    );
    if (!this.isComparisonEstimate)
      referenceImages.forEach((image, index) => {
        const imageUrl = image.trim();
        if (seenImages.has(imageUrl)) return;
        seenImages.add(imageUrl);
        sourceProducts.push({
          id: `request-photo-${index + 1}`,
          productId: "",
          name: `고객 첨부 사진 ${index + 1}`,
          imageUrl,
        });
      });
    this.replyForm.products = sourceProducts.map((product) => ({
      id: product.id,
      productId: product.productId || "",
      name: product.name || "",
      description: "",
      imageUrl: product.imageUrl || "",
      imageUrls: product.imageUrl ? [product.imageUrl] : [],
      price:
        (storedPrices.find((item) => item.productId === product.id) || {})
          .price ||
        (sourceProducts.length === 1 && this.estimate.status === "response"
          ? normalizeEstimateReplyPrice(reply.price || this.estimate.price)
          : ""),
      material: { base: "14K", additionalPrice: "" },
      color: "white",
      diamonds: [],
      promotion: { enabled: false, price: "" },
    }));
    this.setReplyProducts(this.replyForm.products);
  },
  setReplyProducts(products, preservePrice) {
    this.replyForm.products = products;
    if (!preservePrice) {
      const minimumPrice = getReplyProductsMinimumPrice(products);
      this.replyForm.price = minimumPrice
        ? `${formatPriceWithCommas(minimumPrice)}원`
        : "";
    }
    this.replyForm.preferredProductPrices = products
      .filter((product) => product.productId)
      .map((product) => ({
        productId: product.productId,
        price: normalizeEstimateReplyPrice(product.price),
      }));
  },
  preferredProductPriceDisplay(productId) {
    const item = this.replyForm.preferredProductPrices.find(
      (price) => price.productId === productId
    );
    return formatEstimateReplyPriceInput(item && item.price);
  },
  setPreferredProductPrice(productId, value) {
    const index = this.replyForm.preferredProductPrices.findIndex(
      (price) => price.productId === productId
    );
    const nextProductPrice = {
      productId,
      price: normalizeEstimateReplyPrice(value),
    };
    if (index < 0) {
      this.replyForm.preferredProductPrices.push(nextProductPrice);
    } else {
      this.$set(this.replyForm.preferredProductPrices, index, nextProductPrice);
    }
  },
  initializePreferredProductPrices() {
    const existingPrices = new Map(
      this.replyForm.preferredProductPrices.map((item) => [
        item.productId,
        item.price,
      ])
    );
    this.replyForm.preferredProductPrices = (this.preferredProducts || []).map(
      (product) => ({
        productId: product.id,
        price: existingPrices.get(product.id) || "",
      })
    );
  },
  selectMessageTemplate(template) {
    this.selectedTemplateId = template.id;
    this.replyForm.description = template.message;
  },
  hasEveryPreferredProductPrice() {
    if (
      Array.isArray(this.replyForm.products) &&
      (!this.estimate ||
        this.editingReply ||
        this.estimate.status !== "response" ||
        this.hasReplyProducts)
    ) {
      return (
        this.replyForm.products.length > 0 &&
        this.replyForm.products.every(
          (product) =>
            Boolean(normalizeReplyProductPrice(product.price)) &&
            Boolean(normalizeReplyProductMaterialPrices(product))
        )
      );
    }
    return (this.preferredProducts || []).every((product) => {
      const productPrice = this.replyForm.preferredProductPrices.find(
        (item) => item.productId === product.id
      );
      return productPrice && isValidEstimateReplyPrice(productPrice.price);
    });
  },
  async updatePreferredProductPrices() {
    if (this.isComparisonEstimate && this.estimate.receivingEnabled === false) {
      return this.$alert("고객이 견적 받기를 중지했습니다.");
    }
    if (!this.hasEveryPreferredProductPrice()) {
      return this.$alert("선호 제품 사진마다 견적가를 입력해주세요.");
    }
    this.savingPreferredProductPrices = true;
    try {
      const res = await updateEstimatePreferredProductPrices(
        this.$route.params.id,
        this.replyForm.preferredProductPrices
      );
      if (res.data.status === 200 && res.data.data) {
        this.$set(
          this.estimate.reply,
          "preferredProductPrices",
          res.data.data.preferredProductPrices
        );
        return this.$alert("제품별 견적가를 고객에게 전달했습니다.", {
          confirmButtonText: "확인",
        });
      }
      return this.$alert(
        res.data.message || "제품별 견적가를 저장할 수 없습니다.",
        {
          title: "저장 실패",
          confirmButtonText: "확인",
        }
      );
    } catch (err) {
      return this.$alert("제품별 견적가 저장에 실패했습니다.", {
        title: "저장 실패",
        confirmButtonText: "확인",
      });
    } finally {
      this.savingPreferredProductPrices = false;
    }
  },
  async submitReply() {
    if (this.submitting || this.uploadingProductImage) return;
    if (this.isComparisonEstimate && this.estimate.receivingEnabled === false) {
      return this.$alert("고객이 견적 받기를 중지했습니다.");
    }
    if (this.replyForm.products.length > 10) {
      return this.$alert(
        "제품은 최대 10개까지 회신할 수 있습니다. 회신할 제품을 선택해주세요."
      );
    }
    if (!this.replyForm.products.length) {
      return this.$alert(
        this.isComparisonEstimate
          ? "고객 요청과 비슷한 제품을 하나 이상 추가해주세요."
          : "제품을 하나 이상 추가해주세요."
      );
    }
    if (
      this.isComparisonEstimate &&
      this.replyForm.products.some(
        (product) => !product.imageUrl || !product.imageUrls.length
      )
    ) {
      return this.$alert("비슷한 제품마다 제품 사진을 첨부해주세요.");
    }
    if (!this.hasEveryPreferredProductPrice()) {
      return this.$alert("모든 제품에 유효한 견적가를 입력해주세요.");
    }
    const invalidPromotionIndex = this.replyForm.products.findIndex(
      (product) => {
        const promotion = product.promotion || {};
        return (
          promotion.enabled &&
          (!/^\d+$/.test(promotion.price) ||
            !normalizeReplyProductPrice(promotion.price))
        );
      }
    );
    if (invalidPromotionIndex >= 0) {
      return this.$alert(
        `제품 ${invalidPromotionIndex + 1}의 프로모션 혜택가를 입력해주세요.`
      );
    }
    this.submitting = true;
    try {
      const replyPayload = { ...this.replyForm };
      delete replyPayload.optionPrices;
      delete replyPayload.includedServices;
      delete replyPayload.specialPromotion;
      delete replyPayload.specialPromotionPrice;
      replyPayload.products = replyPayload.products.map((product) => ({
        ...product,
        promotion: {
          enabled: !!(product.promotion && product.promotion.enabled),
          price:
            product.promotion && product.promotion.enabled
              ? product.promotion.price
              : "",
        },
      }));
      if (this.editingReply) {
        delete replyPayload.images;
        replyPayload.expectedUpdatedAt = this.estimate.updatedAt;
      }
      const res = this.editingReply
        ? await updateEstimateReply(this.$route.params.id, replyPayload)
        : await replyToEstimate(this.$route.params.id, replyPayload);
      if (res.data.status === 200) {
        if (this.editingReply) {
          this.editingReply = false;
          await this.loadEstimate();
          return this.$alert("견적 수정이 완료되었습니다.", {
            confirmButtonText: "확인",
          });
        }
        this.$alert("견적 회신이 완료되었습니다.", {
          confirmButtonText: "확인",
          callback: () => this.$router.back(),
        });
      } else {
        this.$alert(res.data.message || "견적을 저장할 수 없습니다.", {
          title: this.editingReply ? "수정 실패" : "회신 실패",
          confirmButtonText: "확인",
        });
      }
    } catch (err) {
      const data = err.response && err.response.data;
      this.$alert((data && data.message) || "견적 저장에 실패했습니다.", {
        title: this.editingReply ? "수정 실패" : "회신 실패",
        confirmButtonText: "확인",
      });
    } finally {
      this.submitting = false;
    }
  },
};
